import { useState, useEffect, useMemo } from 'react';

export const usePaymentsManagement = (formik) => {
  
  useEffect(() => {
    if (formik.values.payments && formik.values.payments.length > 0) {
      const updatedPayments = [...formik.values.payments];
      let hasChanges = false;

      updatedPayments.forEach((payment, paymentIndex) => {
        payment.unitAmounts?.forEach((unitAmount, unitIndex) => {
          if (unitAmount.unit_nr) {
            const unitInUnitsArray = formik.values.units?.find(u => u.unit_nr === unitAmount.unit_nr);
            const latestSettledPrice = unitInUnitsArray?.settled_price || unitAmount.settled_price;
            
            if (unitAmount.settled_price !== latestSettledPrice) {
              unitAmount.settled_price = latestSettledPrice;
              hasChanges = true;
            }

            let totalPaidForUnit = 0;
            
            updatedPayments.forEach((p) => {
              const matchingUnit = p.unitAmounts?.find(u => u.unit_nr === unitAmount.unit_nr);
              if (matchingUnit && matchingUnit.amount) {
                totalPaidForUnit += parseInt(matchingUnit.amount) || 0;
              }
            });

            const settledPrice = parseInt(latestSettledPrice) || 0;
            const newRemaining = Math.max(0, settledPrice - totalPaidForUnit);
            
            if (unitAmount.remaining !== newRemaining) {
              unitAmount.remaining = newRemaining;
              hasChanges = true;
            }
          }
        });
      });

      if (hasChanges) {
        formik.setFieldValue("payments", updatedPayments);
      }
    }
  }, [
    formik.values.payments?.length, 
    JSON.stringify(formik.values.payments?.map(p => 
      p.unitAmounts?.map(u => ({ unit_nr: u.unit_nr, amount: u.amount }))
    )),
    JSON.stringify(formik.values.units?.map(u => ({ 
      unit_nr: u.unit_nr, 
      settled_price: u.settled_price 
    })))
  ]);

  const getTotalPayments = useMemo(() => {
    return () => {
      return formik.values.payments.reduce((total, payment) => {
        const unitAmounts = payment.unitAmounts || [];
        const paymentTotal = unitAmounts.reduce(
          (sum, item) => sum + parseFloat(item.amount || 0),
          0
        );
        return total + paymentTotal;
      }, 0);
    };
  }, [formik.values.payments]);

  const addPayment = () => {
    const unitAmounts = formik.values.units.map((unit, index) => {
      return {
        unit_id: unit.unitId,
        unit_nr: unit.unit_nr,
        unit_type: unit.unit_type,
        settled_price: unit.settled_price,
        amount: "",
        remaining: parseInt(unit.settled_price) || 0,
      };
    });

      formik.setFieldValue("payments", [
        ...formik.values.payments,
        {
          type: "",
          vat_percentage:
            formik.values.payments.length > 0
              ? formik.values.payments[0].vat_percentage
              : 19,
          booking_date: "",
          isPaid: false,
          cashed_date: null,
          unitAmounts: unitAmounts,
        },
      ]);
  };

  const handlePaymentTypeChange = (index, value) => {
    const updatedPayments = [...formik.values.payments];
    updatedPayments[index].type = value;
    formik.setFieldValue("payments", updatedPayments);
  };

  const handlePaymentDateChange = (index, date) => {
    const updatedPayments = [...formik.values.payments];
    updatedPayments[index].booking_date = date;
    formik.setFieldValue("payments", updatedPayments);
  };

  const handleUnitAmountChange = (paymentIndex, unitIndex, amount) => {
    const updatedPayments = [...formik.values.payments];
    const rawValue = amount.replace(/[^0-9]/g, "");
    updatedPayments[paymentIndex].unitAmounts[unitIndex].amount = rawValue;
    formik.setFieldValue("payments", updatedPayments);
  };

  const handlePaymentStatusChange = (paymentIndex, isPaid) => {
    const updatedPayments = [...formik.values.payments];
    const payment = updatedPayments[paymentIndex];
    console.log("payment : ", payment)
    payment.isPaid = isPaid;
    
    if (isPaid) {
      payment.cashed_date = payment.booking_date;
      payment.cashed = payment.cashed || payment.amount;
      payment.unitAmounts = payment.unitAmounts.map(unitAmount => ({
        ...unitAmount,
        cashed: parseInt(unitAmount.amount) || null
      }));
    } else {
      payment.cashed_date = null;
      payment.cashed = null;
      payment.unitAmounts = payment.unitAmounts.map(unitAmount => ({
        ...unitAmount,
        cashed: null
      }));
    }
    
    formik.setFieldValue("payments", updatedPayments);
  };

  const getPaymentTotal = (payment) => {
    const unitAmounts = payment.unitAmounts || [];
    return unitAmounts.reduce(
      (sum, item) => sum + (parseInt(item.amount) || 0),
      0
    );
  };

  const deletePayment = (index) => {
    const updatedPayments = formik.values.payments.filter(
      (_, i) => i !== index
    );
    formik.setFieldValue("payments", updatedPayments);
  };

  const handleVATChange = (value) => {
    const updatedPayments = formik.values.payments.map((payment) => ({
      ...payment,
      vat_percentage: parseInt(value),
    }));

    formik.setFieldValue("payments", updatedPayments);
  };

  const getTotalSettlementPrice = (units) => {
    if (formik.values.isManuallyEdited) {
      return true;
    }

    let totalSettledPrice = 0;
    let totalExtraPrice = 0;

    units.forEach((unit) => {
      totalSettledPrice += parseFloat(unit?.settled_price) || 0;
      if (unit.showExtras) {
        totalExtraPrice += parseFloat(unit?.extras) || 0;
      }
    });
    
    formik.setFieldValue("settled_price", totalSettledPrice + totalExtraPrice);
    formik.setFieldValue("extra_payments", totalExtraPrice);
    return true;
  };

  const calculatePaymentPercentages = () => {
    const totalPayments = getTotalPayments();
    const settledPrice = formik.values.settled_price;

    const totalPaidPercentage = settledPrice
      ? ((totalPayments / parseInt(settledPrice)) * 100).toFixed(1)
      : 0;

    const remainingPercentage = settledPrice
      ? (100 - (totalPayments / parseInt(settledPrice)) * 100).toFixed(1)
      : 100;

    return { totalPaidPercentage, remainingPercentage };
  };

  const getLatestSettlementPrice = (unitNr) => {
    const unit = formik.values.units?.find(u => u.unit_nr === unitNr);
    return parseInt(unit?.settled_price) || 0;
  };

  const getRemainingForUnit = (unitNr) => {
    for (const payment of formik.values.payments) {
      const unitAmount = payment.unitAmounts?.find(u => u.unit_nr === unitNr);
      if (unitAmount) {
        return unitAmount.remaining || 0;
      }
    }
    return getLatestSettlementPrice(unitNr);
  };

  const isAmountExceedingLimit = (unitNr, newAmount) => {
    const remaining = getRemainingForUnit(unitNr);
    return parseInt(newAmount || 0) > remaining;
  };

  const getCurrentSettlementPrice = (unitNr) => {
    return getLatestSettlementPrice(unitNr);
  };

  useEffect(() => {
    formik.setFieldValue(
      "remaining_balance",
      formik.values.settled_price - getTotalPayments()
    );
  }, [formik.values.payments, formik.values.settled_price]);

  useEffect(() => {
    if (!formik.values.isManuallyEdited) {
      getTotalSettlementPrice(formik.values.units);
    }
  }, [formik.values.units]);

  return {
    getTotalPayments,
    addPayment,
    handlePaymentTypeChange,
    handlePaymentDateChange,
    handleUnitAmountChange,
    handlePaymentStatusChange,
    getPaymentTotal,
    deletePayment,
    handleVATChange,
    getTotalSettlementPrice,
    calculatePaymentPercentages,
    getRemainingForUnit,
    isAmountExceedingLimit,
    getCurrentSettlementPrice,
    getLatestSettlementPrice
  };
};